import React, { useEffect, useMemo, useRef, useState } from "react";
import { utils as XLSXUtils, writeFile } from "xlsx";
import { adminApiFetch, getAdminRole } from "./api";

const formatDate = (value) => {
    if (!value) return "Unknown date";
    // Match the date display used in Order Management (ISO-like YYYY-MM-DD).
    const raw = String(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
        return raw.slice(0, 10);
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return raw;
    return parsed.toISOString().slice(0, 10);
};

export const aggregateRevenueByDay = (orders = []) => {
    const revenueByDate = new Map();
    // Track distinct order_ids per day to compute bookingCount.
    const orderIdsByDate = new Map();

    orders.forEach((order) => {
        const dateKey = order?.appointment_date || "unknown";
        const details = Array.isArray(order?.details) ? order.details : [];

        const orderRevenue = details.reduce((sum, item) => {
            const quantity = Number(item?.quantity || 0);
            const price = Number(item?.price || 0);
            if (!Number.isFinite(quantity) || !Number.isFinite(price)) {
                return sum;
            }
            return sum + (quantity * price);
        }, 0);

        const current = revenueByDate.get(dateKey) || { date: dateKey, revenue: 0, itemCount: 0, bookingCount: 0 };
        current.revenue += orderRevenue;
        current.itemCount += 1;
        revenueByDate.set(dateKey, current);

        // Count distinct order_ids as bookings.
        if (!orderIdsByDate.has(dateKey)) orderIdsByDate.set(dateKey, new Set());
        const orderId = order?.order_id;
        if (orderId != null) orderIdsByDate.get(dateKey).add(orderId);
    });

    revenueByDate.forEach((row, dateKey) => {
        row.bookingCount = orderIdsByDate.get(dateKey)?.size ?? 0;
    });

    return [...revenueByDate.values()].sort((a, b) => String(b.date).localeCompare(String(a.date)));
};

const parseIsoDate = (value) => {
    if (!value || typeof value !== "string") return null;
    const parsed = new Date(`${value}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const filterRevenueRowsByDateRange = (rows = [], fromDate = "", toDate = "") => {
    const from = parseIsoDate(fromDate);
    const to = parseIsoDate(toDate);

    return rows.filter((row) => {
        const rowDate = parseIsoDate(row?.date);
        if (!rowDate) return false;
        if (from && rowDate < from) return false;
        if (to && rowDate > to) return false;
        return true;
    });
};

export const sortRevenueRowsByDate = (rows = [], sortOrder = "desc") => {
    const sorted = [...rows].sort((a, b) => String(a.date).localeCompare(String(b.date)));
    return sortOrder === "asc" ? sorted : sorted.reverse();
};

const formatMonth = (monthKey) => {
    if (!monthKey) return "Unknown month";
    const raw = String(monthKey);
    if (/^\d{4}-\d{2}/.test(raw)) {
        return raw.slice(0, 7);
    }
    const parsed = new Date(`${raw}-01T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return raw;
    return parsed.toISOString().slice(0, 7);
};

export const aggregateRevenueByMonth = (rows = []) => {
    const monthMap = new Map();
    rows.forEach((row) => {
        const month = String(row?.date || "").slice(0, 7);
        if (!/^\d{4}-\d{2}$/.test(month)) return;
        const current = monthMap.get(month) || { month, revenue: 0, itemCount: 0, bookingCount: 0, dayCount: 0 };
        current.revenue += Number(row?.revenue || 0);
        current.itemCount += Number(row?.itemCount || 0);
        current.bookingCount += Number(row?.bookingCount || 0);
        current.dayCount += 1;
        monthMap.set(month, current);
    });
    return [...monthMap.values()].sort((a, b) => String(a.month).localeCompare(String(b.month)));
};

const escapeCsvValue = (value) => {
    const raw = String(value ?? "");
    if (raw.includes(",") || raw.includes("\n") || raw.includes("\"")) {
        return `"${raw.replace(/\"/g, "\"\"")}"`;
    }
    return raw;
};

export const buildAccountingCsv = (rows = []) => {
    const header = ["date", "bookings", "order_items", "revenue_idr"];
    const dataRows = rows.map((row) => [row.date, row.bookingCount, row.itemCount, row.revenue]);
    return [header, ...dataRows]
        .map((line) => line.map(escapeCsvValue).join(","))
        .join("\n");
};

export const getSelectedRevenueRows = (rows = [], selectedDates = []) => {
    if (!Array.isArray(selectedDates) || selectedDates.length === 0) return [];
    const selectedDateSet = new Set(selectedDates);
    return rows.filter((row) => selectedDateSet.has(row?.date));
};

export const getSelectedMonthRows = (rows = [], selectedMonths = []) => {
    if (!Array.isArray(selectedMonths) || selectedMonths.length === 0) return [];
    const selectedMonthSet = new Set(selectedMonths);
    return rows.filter((row) => selectedMonthSet.has(row?.month));
};

export const buildMonthlyAccountingCsv = (rows = []) => {
    const header = ["month", "day_count", "bookings", "order_items", "revenue_idr"];
    const dataRows = rows.map((row) => [formatMonth(row.month), row.dayCount, row.bookingCount, row.itemCount, row.revenue]);
    return [header, ...dataRows]
        .map((line) => line.map(escapeCsvValue).join(","))
        .join("\n");
};

export const buildDetailedDailyRows = (selectedDates = [], orders = [], guests = []) => {
    if (selectedDates.length === 0 || orders.length === 0) return [];
    const selectedDateSet = new Set(selectedDates);
    const guestMap = new Map(
        guests.map((g) => [g.id, [g.first_name, g.last_name].filter(Boolean).join(" ")])
    );
    const rows = [];
    orders
        .filter((o) => selectedDateSet.has(o.appointment_date))
        .forEach((order) => {
            const detail = Array.isArray(order.details) ? order.details[0] : null;
            if (!detail || detail.name === "time_slot_blocker") return;
            const price = Number(detail.price) || 0;
            const qty = Number(detail.quantity) || 0;
            const discount = Number(detail.discount) || 0;
            rows.push({
                date: order.appointment_date,
                order_id: order.order_id ?? "",
                guest: guestMap.get(order.guest_id) || `Guest #${order.guest_id}`,
                appointment_time: order.appointment_time ?? "",
                notes: order.notes ?? "",
                item: detail.name ?? "",
                category: detail.category ?? "",
                quantity: qty,
                unit_price_idr: price,
                discount_idr: discount,
                line_total_idr: price * qty,
            });
        });
    rows.sort((a, b) => {
        const d = String(a.date).localeCompare(String(b.date));
        if (d !== 0) return d;
        const t = String(a.appointment_time).localeCompare(String(b.appointment_time));
        if (t !== 0) return t;
        return String(a.order_id).localeCompare(String(b.order_id));
    });
    return rows;
};

export const buildDetailedDailyCsv = (selectedDates = [], orders = [], guests = []) => {
    const rows = buildDetailedDailyRows(selectedDates, orders, guests);
    if (rows.length === 0) return "";
    const header = ["date", "order_id", "guest", "appointment_time", "notes", "item", "category", "quantity", "unit_price_idr", "discount_idr", "line_total_idr"];
    const dataRows = rows.map((row) => [
        row.date,
        row.order_id,
        row.guest,
        row.appointment_time,
        row.notes,
        row.item,
        row.category,
        row.quantity,
        row.unit_price_idr,
        row.discount_idr,
        row.line_total_idr,
    ]);
    return [header, ...dataRows].map((line) => line.map(escapeCsvValue).join(",")).join("\n");
};

export const buildDetailedMonthlyRows = (selectedMonths = [], orders = [], guests = []) => {
    if (selectedMonths.length === 0 || orders.length === 0) return [];
    const selectedMonthSet = new Set(selectedMonths);
    const guestMap = new Map(
        guests.map((g) => [g.id, [g.first_name, g.last_name].filter(Boolean).join(" ")])
    );
    const rows = [];
    orders
        .filter((o) => selectedMonthSet.has(String(o.appointment_date || "").slice(0, 7)))
        .forEach((order) => {
            const detail = Array.isArray(order.details) ? order.details[0] : null;
            if (!detail || detail.name === "time_slot_blocker") return;
            const price = Number(detail.price) || 0;
            const qty = Number(detail.quantity) || 0;
            const discount = Number(detail.discount) || 0;
            const month = String(order.appointment_date || "").slice(0, 7);
            rows.push({
                month,
                date: order.appointment_date,
                order_id: order.order_id ?? "",
                guest: guestMap.get(order.guest_id) || `Guest #${order.guest_id}`,
                appointment_time: order.appointment_time ?? "",
                notes: order.notes ?? "",
                item: detail.name ?? "",
                category: detail.category ?? "",
                quantity: qty,
                unit_price_idr: price,
                discount_idr: discount,
                line_total_idr: price * qty,
            });
        });
    rows.sort((a, b) => {
        const m = String(a.month).localeCompare(String(b.month));
        if (m !== 0) return m;
        const d = String(a.date).localeCompare(String(b.date));
        if (d !== 0) return d;
        const t = String(a.appointment_time).localeCompare(String(b.appointment_time));
        if (t !== 0) return t;
        return String(a.order_id).localeCompare(String(b.order_id));
    });
    return rows;
};

export const buildDetailedMonthlyCsv = (selectedMonths = [], orders = [], guests = []) => {
    const rows = buildDetailedMonthlyRows(selectedMonths, orders, guests);
    if (rows.length === 0) return "";
    const header = ["month", "date", "order_id", "guest", "appointment_time", "notes", "item", "category", "quantity", "unit_price_idr", "discount_idr", "line_total_idr"];
    const dataRows = rows.map((row) => [
        row.month,
        row.date,
        row.order_id,
        row.guest,
        row.appointment_time,
        row.notes,
        row.item,
        row.category,
        row.quantity,
        row.unit_price_idr,
        row.discount_idr,
        row.line_total_idr,
    ]);
    return [header, ...dataRows].map((line) => line.map(escapeCsvValue).join(",")).join("\n");
};

const detailsFromOrders = (orders = []) => {
    return orders
        .map((order) => {
            const detail = Array.isArray(order.details) ? order.details[0] : null;
            if (!detail || detail.name === "time_slot_blocker") return null;
            const quantity = Number(detail.quantity) || 0;
            const unitPrice = Number(detail.price) || 0;
            const discount = Number(detail.discount) || 0;
            return {
                date: order.appointment_date,
                month: String(order.appointment_date || "").slice(0, 7),
                time: order.appointment_time,
                villaId: order.villa_id,
                guestId: order.guest_id,
                orderId: order.order_id,
                itemName: detail.name,
                category: detail.category || "unknown",
                quantity,
                unitPrice,
                discount,
                lineTotal: quantity * unitPrice,
            };
        })
        .filter(Boolean);
};

export const buildCategoryBreakdown = (orders = []) => {
    const rows = detailsFromOrders(orders);
    const totalRevenue = rows.reduce((sum, row) => sum + row.lineTotal, 0);
    const totalItems = rows.reduce((sum, row) => sum + row.quantity, 0);
    const byCategory = new Map();
    rows.forEach((row) => {
        const key = row.category;
        const current = byCategory.get(key) || { category: key, revenue: 0, items: 0 };
        current.revenue += row.lineTotal;
        current.items += row.quantity;
        byCategory.set(key, current);
    });
    return [...byCategory.values()]
        .map((row) => ({
            ...row,
            revenueSharePct: totalRevenue > 0 ? (row.revenue / totalRevenue) * 100 : 0,
            itemSharePct: totalItems > 0 ? (row.items / totalItems) * 100 : 0,
        }))
        .sort((a, b) => b.revenue - a.revenue);
};

export const buildItemPerformance = (orders = []) => {
    const rows = detailsFromOrders(orders);
    const byItem = new Map();
    rows.forEach((row) => {
        const key = row.itemName;
        const current = byItem.get(key) || { itemName: key, category: row.category, itemsSold: 0, revenue: 0, orderIds: new Set() };
        current.itemsSold += row.quantity;
        current.revenue += row.lineTotal;
        if (row.orderId != null) current.orderIds.add(row.orderId);
        byItem.set(key, current);
    });
    const itemRows = [...byItem.values()].map((row) => ({
        itemName: row.itemName,
        category: row.category,
        itemsSold: row.itemsSold,
        revenue: row.revenue,
        orderCount: row.orderIds.size,
    }));
    return {
        topBySold: [...itemRows].sort((a, b) => b.itemsSold - a.itemsSold).slice(0, 8),
        topByRevenue: [...itemRows].sort((a, b) => b.revenue - a.revenue).slice(0, 8),
        flopBySold: [...itemRows].filter((r) => r.itemsSold > 0).sort((a, b) => a.itemsSold - b.itemsSold).slice(0, 8),
    };
};

export const buildTimeSlotAnalysis = (orders = []) => {
    const rows = detailsFromOrders(orders);
    const bySlot = new Map();
    rows.forEach((row) => {
        const key = row.time || "unknown";
        const current = bySlot.get(key) || { slot: key, revenue: 0, items: 0, orderIds: new Set() };
        current.revenue += row.lineTotal;
        current.items += row.quantity;
        if (row.orderId != null) current.orderIds.add(row.orderId);
        bySlot.set(key, current);
    });
    const slots = [...bySlot.values()]
        .map((row) => ({
            slot: row.slot,
            revenue: row.revenue,
            items: row.items,
            bookings: row.orderIds.size,
        }))
        .sort((a, b) => String(a.slot).localeCompare(String(b.slot)));
    const peakRevenue = [...slots].sort((a, b) => b.revenue - a.revenue)[0] || null;
    const peakBookings = [...slots].sort((a, b) => b.bookings - a.bookings)[0] || null;
    return { slots, peakRevenue, peakBookings };
};

export const buildGuestAnalysis = (orders = [], guests = []) => {
    const rows = detailsFromOrders(orders);
    const guestMap = new Map(guests.map((g) => [g.id, g]));
    const byGuest = new Map();
    rows.forEach((row) => {
        const current = byGuest.get(row.guestId) || {
            guestId: row.guestId,
            guestName: "",
            villaId: row.villaId,
            revenue: 0,
            items: 0,
            orderIds: new Set(),
            visitDays: new Set(),
        };
        const guest = guestMap.get(row.guestId);
        current.guestName = guest ? [guest.first_name, guest.last_name].filter(Boolean).join(" ") : `Guest #${row.guestId}`;
        current.revenue += row.lineTotal;
        current.items += row.quantity;
        if (row.orderId != null) current.orderIds.add(row.orderId);
        if (row.date) current.visitDays.add(row.date);
        byGuest.set(row.guestId, current);
    });
    const guestRows = [...byGuest.values()].map((row) => ({
        guestId: row.guestId,
        guestName: row.guestName,
        villaId: row.villaId,
        revenue: row.revenue,
        items: row.items,
        bookingCount: row.orderIds.size,
        visitDays: row.visitDays.size,
        guestType: row.orderIds.size > 1 ? "returning" : "new",
    }));
    const byVilla = new Map();
    guestRows.forEach((row) => {
        const key = row.villaId ?? "unknown";
        const current = byVilla.get(key) || { villaId: key, guests: 0, revenue: 0, bookings: 0 };
        current.guests += 1;
        current.revenue += row.revenue;
        current.bookings += row.bookingCount;
        byVilla.set(key, current);
    });
    return {
        topGuests: [...guestRows].sort((a, b) => b.revenue - a.revenue).slice(0, 10),
        newGuests: guestRows.filter((row) => row.guestType === "new").length,
        returningGuests: guestRows.filter((row) => row.guestType === "returning").length,
        byVilla: [...byVilla.values()].sort((a, b) => b.revenue - a.revenue),
    };
};

const getIsoWeekKey = (dateValue) => {
    const d = new Date(`${dateValue}T00:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
    return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
};

export const buildTrendComparison = (dailyRows = [], monthlyRows = []) => {
    const sortedMonths = [...monthlyRows].sort((a, b) => String(a.month).localeCompare(String(b.month)));
    const latestMonth = sortedMonths[sortedMonths.length - 1] || null;
    const prevMonth = sortedMonths[sortedMonths.length - 2] || null;
    const momPct = latestMonth && prevMonth && prevMonth.revenue > 0
        ? ((latestMonth.revenue - prevMonth.revenue) / prevMonth.revenue) * 100
        : null;

    const byWeek = new Map();
    dailyRows.forEach((row) => {
        const key = getIsoWeekKey(row.date);
        if (!key) return;
        byWeek.set(key, (byWeek.get(key) || 0) + (Number(row.revenue) || 0));
    });
    const weeks = [...byWeek.entries()].sort((a, b) => String(a[0]).localeCompare(String(b[0])));
    const latestWeek = weeks[weeks.length - 1] || null;
    const prevWeek = weeks[weeks.length - 2] || null;
    const wowPct = latestWeek && prevWeek && prevWeek[1] > 0
        ? ((latestWeek[1] - prevWeek[1]) / prevWeek[1]) * 100
        : null;

    return {
        latestMonth,
        prevMonth,
        momPct,
        latestWeek,
        prevWeek,
        wowPct,
    };
};

export const buildMonthEndForecast = (dailyRows = [], now = new Date()) => {
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const monthRows = dailyRows.filter((row) => String(row.date || "").slice(0, 7) === currentMonth);
    if (monthRows.length === 0) {
        return { month: currentMonth, revenueSoFar: 0, elapsedDays: now.getDate(), totalDays: new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate(), projectedMonthEndRevenue: 0 };
    }
    const revenueSoFar = monthRows.reduce((sum, row) => sum + (Number(row.revenue) || 0), 0);
    const elapsedDays = Math.max(1, now.getDate());
    const totalDays = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const projectedMonthEndRevenue = (revenueSoFar / elapsedDays) * totalDays;
    return { month: currentMonth, revenueSoFar, elapsedDays, totalDays, projectedMonthEndRevenue };
};

function DayDetailPanel({ date, orderGroups, getGuestName }) {
    if (orderGroups.length === 0) {
        return (
            <div className="accounting-detail-panel">
                <p style={{ color: "#888", margin: 0 }}>No order details available for {date}.</p>
            </div>
        );
    }
    return (
        <div className="accounting-detail-panel">
            {orderGroups.map((group) => {
                const guestName = getGuestName(group.guest_id);
                return (
                    <div key={group.order_id} className="accounting-order-group">
                        <div className="accounting-order-group-header">
                            <span className="accounting-order-group-guest">👤 {guestName}</span>
                            <span className="accounting-order-group-time">🕐 {group.appointment_time}</span>
                            {group.notes && (
                                <span className="accounting-order-group-notes">📝 {group.notes}</span>
                            )}
                            <span className="accounting-order-group-subtotal">
                                Subtotal: <strong>{group.subtotal.toLocaleString("en-US")} IDR</strong>
                            </span>
                        </div>
                        {group.items.length > 0 ? (
                            <table className="accounting-detail-table">
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th>Category</th>
                                        <th>Quantity</th>
                                        <th>Unit Price</th>
                                        <th>Discount</th>
                                        <th>Line Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.items.map((item, idx) => {
                                        const price = Number(item.price) || 0;
                                        const qty = Number(item.quantity) || 0;
                                        const discount = Number(item.discount) || 0;
                                        const lineTotal = price * qty;
                                        return (
                                            <tr key={idx}>
                                                <td>{item.name || "—"}</td>
                                                <td>{item.category || "—"}</td>
                                                <td>{qty}</td>
                                                <td>
                                                    {price === 0 && discount > 0
                                                        ? <span style={{ color: "#2a7a2a" }}>included</span>
                                                        : `${price.toLocaleString("en-US")} IDR`}
                                                </td>
                                                <td>
                                                    {discount > 0
                                                        ? <span style={{ color: "#2a7a2a" }}>{discount.toLocaleString("en-US")} IDR</span>
                                                        : "—"}
                                                </td>
                                                <td>{lineTotal.toLocaleString("en-US")} IDR</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <p style={{ color: "#888", fontSize: "0.85rem", margin: "4px 0 0" }}>No items.</p>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function MonthDetailPanel({ month, days, getOrderGroupsForDate, getGuestName }) {
    if (days.length === 0) {
        return (
            <div className="accounting-detail-panel">
                <p style={{ color: "#888", margin: 0 }}>No order details available for {month}.</p>
            </div>
        );
    }
    return (
        <div className="accounting-detail-panel">
            {days.map((date) => {
                const orderGroups = getOrderGroupsForDate(date);
                return (
                    <div key={date} className="accounting-month-day-block">
                        <div className="accounting-month-day-title">📅 {date}</div>
                        <DayDetailPanel
                            date={date}
                            orderGroups={orderGroups}
                            getGuestName={getGuestName}
                        />
                    </div>
                );
            })}
        </div>
    );
}

function SectionHeader({ title, badges = [], children }) {
    return (
        <div className="accounting-section-header">
            <div className="accounting-section-header-left">
                <h3 className="accounting-section-title">{title}</h3>
                {badges.length > 0 && (
                    <div className="management-badge-row">
                        {badges.map((badge) => (
                            <span key={badge.label} className={`management-badge ${badge.className || "management-badge-neutral"}`}>
                                {badge.label}
                            </span>
                        ))}
                    </div>
                )}
            </div>
            {children && <div className="accounting-section-header-actions">{children}</div>}
        </div>
    );
}

function SelectionHint({ count, totalRevenue, entityLabel, clearLabel, onClear }) {
    if (count === 0) return null;
    return (
        <div className="accounting-selection-hint">
            <span>
                ✓ <strong>{count}</strong> {entityLabel} selected
                {" "}&mdash; Selected revenue: <strong>{totalRevenue.toLocaleString("en-US")} IDR</strong>
            </span>
            <button type="button" className="accounting-hint-clear-btn" onClick={onClear}>
                {clearLabel}
            </button>
        </div>
    );
}

function Accounting() {
    const baseUrl = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL || "http://localhost:8000/orders/api";
    const [orders, setOrders] = useState([]);
    const [guests, setGuests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [sortOrder, setSortOrder] = useState("desc");
    const [selectedDates, setSelectedDates] = useState([]);
    const [selectedMonths, setSelectedMonths] = useState([]);
    const [expandedDates, setExpandedDates] = useState(new Set());
    const [expandedMonths, setExpandedMonths] = useState(new Set());
    const [auditEntries, setAuditEntries] = useState([]);
    const [activeTab, setActiveTab] = useState("overview");
    const [exportProfile, setExportProfile] = useState("finance");
    const lastLoadedOrderIdsRef = useRef(new Set());
    const hasLoggedInitialFiltersRef = useRef(false);
    const filterAuditDebounceTimerRef = useRef(null);
    const adminRole = getAdminRole() || "unknown";

    const logAuditEvent = (action, meta = {}) => {
        setAuditEntries((prev) => [
            {
                id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
                timestamp: new Date().toISOString(),
                actor: adminRole,
                action,
                meta,
            },
            ...prev,
        ].slice(0, 300));
    };

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setErrorMsg("");
            try {
                const [ordersRes, guestsRes] = await Promise.all([
                    adminApiFetch(`${baseUrl}/orders`),
                    adminApiFetch(`${baseUrl}/guests`),
                ]);
                if (!ordersRes.ok) throw new Error("Revenue data could not be loaded.");
                if (!guestsRes.ok) throw new Error("Guest data could not be loaded.");
                const [ordersData, guestsData] = await Promise.all([ordersRes.json(), guestsRes.json()]);
                setOrders(Array.isArray(ordersData) ? ordersData : []);
                setGuests(Array.isArray(guestsData) ? guestsData : []);

                const newOrderIds = new Set((Array.isArray(ordersData) ? ordersData : []).map((o) => o.order_id).filter((id) => id != null));
                if (lastLoadedOrderIdsRef.current.size > 0) {
                    const before = lastLoadedOrderIdsRef.current.size;
                    const after = newOrderIds.size;
                    if (before !== after) {
                        logAuditEvent("orders_data_changed", { before, after, delta: after - before });
                    }
                }
                lastLoadedOrderIdsRef.current = newOrderIds;
            } catch (error) {
                setErrorMsg(error.message || "Data could not be loaded.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [baseUrl]);

    const dailyRevenue = useMemo(() => aggregateRevenueByDay(orders), [orders]);
    const filteredDailyRevenue = useMemo(
        () => filterRevenueRowsByDateRange(dailyRevenue, fromDate, toDate),
        [dailyRevenue, fromDate, toDate]
    );
    const sortedFilteredDailyRevenue = useMemo(
        () => sortRevenueRowsByDate(filteredDailyRevenue, sortOrder),
        [filteredDailyRevenue, sortOrder]
    );
    const monthlyRevenue = useMemo(() => {
        const rows = aggregateRevenueByMonth(filteredDailyRevenue);
        return sortOrder === "asc" ? rows : [...rows].reverse();
    }, [filteredDailyRevenue, sortOrder]);

    const totalRevenue = useMemo(
        () => sortedFilteredDailyRevenue.reduce((sum, day) => sum + day.revenue, 0),
        [sortedFilteredDailyRevenue]
    );
    const selectedDailyRevenue = useMemo(
        () => getSelectedRevenueRows(sortedFilteredDailyRevenue, selectedDates),
        [sortedFilteredDailyRevenue, selectedDates]
    );
    const selectedMonthlyRevenue = useMemo(
        () => getSelectedMonthRows(monthlyRevenue, selectedMonths),
        [monthlyRevenue, selectedMonths]
    );

    const filteredOrders = useMemo(() => {
        const allowedDates = new Set(sortedFilteredDailyRevenue.map((row) => row.date));
        return orders.filter((o) => allowedDates.has(o.appointment_date));
    }, [orders, sortedFilteredDailyRevenue]);

    const categoryBreakdown = useMemo(() => buildCategoryBreakdown(filteredOrders), [filteredOrders]);
    const itemPerformance = useMemo(() => buildItemPerformance(filteredOrders), [filteredOrders]);
    const timeSlotAnalysis = useMemo(() => buildTimeSlotAnalysis(filteredOrders), [filteredOrders]);
    const guestAnalysis = useMemo(() => buildGuestAnalysis(filteredOrders, guests), [filteredOrders, guests]);
    const trendComparison = useMemo(() => buildTrendComparison(sortedFilteredDailyRevenue, monthlyRevenue), [sortedFilteredDailyRevenue, monthlyRevenue]);
    const forecast = useMemo(() => buildMonthEndForecast(sortedFilteredDailyRevenue, new Date()), [sortedFilteredDailyRevenue]);

    useEffect(() => {
        setSelectedDates((prev) => {
            const visible = new Set(sortedFilteredDailyRevenue.map((row) => row.date));
            return prev.filter((d) => visible.has(d));
        });
    }, [sortedFilteredDailyRevenue]);

    useEffect(() => {
        setSelectedMonths((prev) => {
            const visible = new Set(monthlyRevenue.map((row) => row.month));
            return prev.filter((m) => visible.has(m));
        });
    }, [monthlyRevenue]);

    useEffect(() => {
        if (!hasLoggedInitialFiltersRef.current) {
            hasLoggedInitialFiltersRef.current = true;
            return;
        }

        if (filterAuditDebounceTimerRef.current) {
            window.clearTimeout(filterAuditDebounceTimerRef.current);
        }

        filterAuditDebounceTimerRef.current = window.setTimeout(() => {
            logAuditEvent("filters_changed", { fromDate, toDate, sortOrder });
        }, 500);

        return () => {
            if (filterAuditDebounceTimerRef.current) {
                window.clearTimeout(filterAuditDebounceTimerRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromDate, toDate, sortOrder]);

    const toggleSelectedDate = (date) => {
        if (!date) return;
        setSelectedDates((prev) =>
            prev.includes(date) ? prev.filter((e) => e !== date) : [...prev, date]
        );
    };
    const toggleSelectedMonth = (month) => {
        if (!month) return;
        setSelectedMonths((prev) =>
            prev.includes(month) ? prev.filter((e) => e !== month) : [...prev, month]
        );
    };

    const toggleExpandedDate = (date) => {
        setExpandedDates((prev) => {
            const next = new Set(prev);
            next.has(date) ? next.delete(date) : next.add(date);
            return next;
        });
    };

    const toggleExpandedMonth = (month) => {
        setExpandedMonths((prev) => {
            const next = new Set(prev);
            next.has(month) ? next.delete(month) : next.add(month);
            return next;
        });
    };

    const getGuestName = (guestId) => {
        const guest = guests.find((g) => g.id === guestId);
        if (!guest) return `Guest #${guestId}`;
        return [guest.first_name, guest.last_name].filter(Boolean).join(" ");
    };

    const getOrderGroupsForDate = (date) => {
        // ...existing code...
        const grouped = new Map();
        orders
            .filter((o) => o.appointment_date === date)
            .forEach((order) => {
                const key = order.order_id ?? `${order.guest_id}_${order.appointment_time}`;
                if (!grouped.has(key)) {
                    grouped.set(key, {
                        order_id: key,
                        guest_id: order.guest_id,
                        appointment_time: order.appointment_time,
                        notes: order.notes,
                        items: [],
                        subtotal: 0,
                    });
                }
                const group = grouped.get(key);
                const detail = Array.isArray(order.details) ? order.details[0] : null;
                if (detail && detail.name !== "time_slot_blocker") {
                    group.items.push(detail);
                    group.subtotal += (Number(detail.price) || 0) * (Number(detail.quantity) || 0);
                }
            });
        return [...grouped.values()].sort((a, b) =>
            String(a.appointment_time).localeCompare(String(b.appointment_time))
        );
    };

    const getDaysForMonth = (month) => {
        // Returns sorted distinct days within the given YYYY-MM month that have orders.
        const days = [...new Set(
            orders
                .filter((o) => String(o.appointment_date || "").slice(0, 7) === month)
                .map((o) => o.appointment_date)
        )].sort();
        return days;
    };

    const downloadCsv = (content, filename) => {
        const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };
    const handleExportCsv = () => {
        const from = fromDate || "all";
        const to = toDate || "all";
        downloadCsv(buildAccountingCsv(selectedDailyRevenue), `accounting_daily_${from}_to_${to}.csv`);
        logAuditEvent("export_csv_daily", { from, to, selectedDays: selectedDates.length });
    };
    const handleExportMonthlyCsv = () => {
        const from = fromDate || "all";
        const to = toDate || "all";
        downloadCsv(buildMonthlyAccountingCsv(selectedMonthlyRevenue), `accounting_monthly_${from}_to_${to}.csv`);
        logAuditEvent("export_csv_monthly", { from, to, selectedMonths: selectedMonths.length });
    };

    const handleExportDetailedDailyCsv = () => {
        const from = fromDate || "all";
        const to = toDate || "all";
        downloadCsv(buildDetailedDailyCsv(selectedDates, orders, guests), `accounting_daily_detailed_${from}_to_${to}.csv`);
        logAuditEvent("export_csv_daily_detailed", { from, to, selectedDays: selectedDates.length });
    };

    const handleExportDetailedMonthlyCsv = () => {
        const from = fromDate || "all";
        const to = toDate || "all";
        downloadCsv(buildDetailedMonthlyCsv(selectedMonths, orders, guests), `accounting_monthly_detailed_${from}_to_${to}.csv`);
        logAuditEvent("export_csv_monthly_detailed", { from, to, selectedMonths: selectedMonths.length });
    };

    const handleExportXlsx = () => {
        const from = fromDate || "all";
        const to = toDate || "all";
        const selectedOrFilteredDates = selectedDates.length > 0 ? selectedDates : sortedFilteredDailyRevenue.map((row) => row.date);
        const selectedOrFilteredMonths = selectedMonths.length > 0 ? selectedMonths : monthlyRevenue.map((row) => row.month);

        const summaryRows = [
            { metric: "total_revenue_idr", value: totalRevenue },
            { metric: "days_in_view", value: sortedFilteredDailyRevenue.length },
            { metric: "months_in_view", value: monthlyRevenue.length },
            { metric: "selected_days", value: selectedDates.length },
            { metric: "selected_months", value: selectedMonths.length },
            { metric: "new_guests", value: guestAnalysis.newGuests },
            { metric: "returning_guests", value: guestAnalysis.returningGuests },
            { metric: "forecast_month_end_idr", value: Math.round(forecast.projectedMonthEndRevenue) },
        ];

        const wb = XLSXUtils.book_new();
        const dailyDetailRows = buildDetailedDailyRows(selectedOrFilteredDates, filteredOrders, guests);
        const monthlyDetailRows = buildDetailedMonthlyRows(selectedOrFilteredMonths, filteredOrders, guests);
        XLSXUtils.book_append_sheet(wb, XLSXUtils.json_to_sheet(summaryRows), "Summary");
        XLSXUtils.book_append_sheet(wb, XLSXUtils.json_to_sheet(dailyDetailRows), "Daily Detail");
        XLSXUtils.book_append_sheet(wb, XLSXUtils.json_to_sheet(monthlyDetailRows), "Monthly Detail");
        XLSXUtils.book_append_sheet(wb, XLSXUtils.json_to_sheet(categoryBreakdown), "Category Breakdown");

        if (exportProfile === "operations") {
            XLSXUtils.book_append_sheet(wb, XLSXUtils.json_to_sheet(timeSlotAnalysis.slots), "Time Slots");
            XLSXUtils.book_append_sheet(wb, XLSXUtils.json_to_sheet(itemPerformance.topBySold), "Top Sold Items");
            XLSXUtils.book_append_sheet(wb, XLSXUtils.json_to_sheet(itemPerformance.flopBySold), "Flop Items");
        }
        if (exportProfile === "finance") {
            XLSXUtils.book_append_sheet(wb, XLSXUtils.json_to_sheet(monthlyRevenue), "Monthly Summary");
            XLSXUtils.book_append_sheet(wb, XLSXUtils.json_to_sheet(auditEntries), "Audit Log");
        }

        writeFile(wb, `accounting_${exportProfile}_${from}_to_${to}.xlsx`);
        logAuditEvent("export_xlsx", { profile: exportProfile, from, to });
    };

    const selectedDailyTotal = useMemo(
        () => selectedDailyRevenue.reduce((sum, row) => sum + row.revenue, 0),
        [selectedDailyRevenue]
    );
    const selectedMonthlyTotal = useMemo(
        () => selectedMonthlyRevenue.reduce((sum, row) => sum + row.revenue, 0),
        [selectedMonthlyRevenue]
    );

    const hasActiveFilters = fromDate || toDate;

    const toYmd = (dateObj) => {
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const day = String(dateObj.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const getQuickPresetRange = (preset, now = new Date()) => {
        const toYmd = (dateObj) => {
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, "0");
            const day = String(dateObj.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
        };

        let from = "";
        let to = "";

        if (preset === "today") {
            from = toYmd(now);
            to = toYmd(now);
        }
        if (preset === "last_7_days") {
            const start = new Date(now);
            start.setDate(now.getDate() - 6);
            from = toYmd(start);
            to = toYmd(now);
        }
        if (preset === "last_30_days") {
            const start = new Date(now);
            start.setDate(now.getDate() - 29);
            from = toYmd(start);
            to = toYmd(now);
        }
        if (preset === "this_month") {
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            from = toYmd(start);
            to = toYmd(now);
        }
        if (preset === "year_to_date") {
            const start = new Date(now.getFullYear(), 0, 1);
            from = toYmd(start);
            to = toYmd(now);
        }
        if (preset === "last_month") {
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const end = new Date(now.getFullYear(), now.getMonth(), 0);
            from = toYmd(start);
            to = toYmd(end);
        }

        return { from, to };
    };

    const quickPresetKeys = ["today", "last_7_days", "last_30_days", "this_month", "year_to_date", "last_month"];
    const quickPresetLabel = {
        today: "Today",
        last_7_days: "Last 7 Days",
        last_30_days: "Last 30 Days",
        this_month: "This Month",
        year_to_date: "Year to Date",
        last_month: "Last Month",
    };

    const activeQuickPreset = useMemo(() => {
        if (!fromDate || !toDate) return "";
        const now = new Date();
        for (const preset of quickPresetKeys) {
            const { from, to } = getQuickPresetRange(preset, now);
            if (fromDate === from && toDate === to) return preset;
        }
        return "";
    }, [fromDate, toDate]);

    const applyQuickPreset = (preset) => {
        const now = new Date();
        const { from, to } = getQuickPresetRange(preset, now);

        setFromDate(from);
        setToDate(to);
        logAuditEvent("quick_preset_applied", { preset, from, to });
    };

    if (isLoading) {
        return (
            <div className="management-page">
                <div className="accounting-loading">
                    <span className="accounting-loading-spinner" aria-hidden="true" />
                    Loading accounting data...
                </div>
            </div>
        );
    }

    return (
        <div className="management-page">
            {/* ── Page Header ─────────────────────────────────────── */}
            <div className="management-page-header">
                <h2 className="management-page-title">Accounting</h2>
                <div className="management-badge-row">
                    <span className="management-badge management-badge-neutral">Days: {sortedFilteredDailyRevenue.length}</span>
                    <span className="management-badge management-badge-neutral">Months: {monthlyRevenue.length}</span>
                    <span className="management-badge management-badge-changed">
                        Total Revenue: {totalRevenue.toLocaleString("en-US")} IDR
                    </span>
                    {hasActiveFilters && (
                        <span className="management-badge management-badge-new">Filter active</span>
                    )}
                    {activeQuickPreset && (
                        <span className="management-badge management-badge-preset">
                            Preset: {quickPresetLabel[activeQuickPreset] || activeQuickPreset}
                        </span>
                    )}
                </div>
            </div>

            <div className="accounting-tab-bar">
                <button
                    type="button"
                    className={`accounting-tab-btn${activeTab === "overview" ? " active" : ""}`}
                    onClick={() => setActiveTab("overview")}
                >
                    Overview
                </button>
                <button
                    type="button"
                    className={`accounting-tab-btn${activeTab === "audit" ? " active" : ""}`}
                    onClick={() => setActiveTab("audit")}
                >
                    Audit Log
                </button>
            </div>

            {activeTab === "overview" && (
                <>

            {/* ── Global Filter Controls ───────────────────────────── */}
            <div className="accounting-filter-card">
                <div className="accounting-filter-card-title">Filters &amp; Sort</div>
                <div className="accounting-quick-presets">
                    <button
                        type="button"
                        className={activeQuickPreset === "today" ? "active" : ""}
                        onClick={() => applyQuickPreset("today")}
                    >
                        Today
                    </button>
                    <button
                        type="button"
                        className={activeQuickPreset === "last_7_days" ? "active" : ""}
                        onClick={() => applyQuickPreset("last_7_days")}
                    >
                        Last 7 Days
                    </button>
                    <button
                        type="button"
                        className={activeQuickPreset === "last_30_days" ? "active" : ""}
                        onClick={() => applyQuickPreset("last_30_days")}
                    >
                        Last 30 Days
                    </button>
                    <button
                        type="button"
                        className={activeQuickPreset === "this_month" ? "active" : ""}
                        onClick={() => applyQuickPreset("this_month")}
                    >
                        This Month
                    </button>
                    <button
                        type="button"
                        className={activeQuickPreset === "year_to_date" ? "active" : ""}
                        onClick={() => applyQuickPreset("year_to_date")}
                    >
                        Year to Date
                    </button>
                    <button
                        type="button"
                        className={activeQuickPreset === "last_month" ? "active" : ""}
                        onClick={() => applyQuickPreset("last_month")}
                    >
                        Last Month
                    </button>
                </div>
                <div className="management-accounting-controls">
                    <label className="management-order-field">
                        From
                        <input type="date" value={fromDate} lang="en-US" onChange={(e) => setFromDate(e.target.value)} />
                    </label>
                    <label className="management-order-field">
                        To
                        <input type="date" value={toDate} lang="en-US" onChange={(e) => setToDate(e.target.value)} />
                    </label>
                    <label className="management-order-field">
                        Sort by Date
                        <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                            <option value="desc">Newest first</option>
                            <option value="asc">Oldest first</option>
                        </select>
                    </label>
                    <div className="management-accounting-actions">
                        <button
                            type="button"
                            onClick={() => { setFromDate(""); setToDate(""); }}
                            disabled={!hasActiveFilters}
                        >
                            Reset Filters
                        </button>
                        <label className="management-order-field" style={{ minWidth: "180px" }}>
                            Export Profile
                            <select
                                value={exportProfile}
                                onChange={(e) => {
                                    setExportProfile(e.target.value);
                                    logAuditEvent("export_profile_changed", { profile: e.target.value });
                                }}
                            >
                                <option value="finance">Finance</option>
                                <option value="operations">Operations</option>
                            </select>
                        </label>
                        <button
                            type="button"
                            className="accounting-export-btn"
                            onClick={handleExportXlsx}
                            disabled={sortedFilteredDailyRevenue.length === 0}
                        >
                            ↓ Export XLSX
                        </button>
                    </div>
                </div>
            </div>

            {errorMsg ? <div className="management-error">{errorMsg}</div> : null}

            {!errorMsg && sortedFilteredDailyRevenue.length === 0 ? (
                <div className="accounting-empty-state">
                    <span className="accounting-empty-icon">📋</span>
                    <p>No orders found{hasActiveFilters ? " for the selected date range." : "."}</p>
                    {hasActiveFilters && (
                        <button type="button" onClick={() => { setFromDate(""); setToDate(""); }}>
                            Clear Filters
                        </button>
                    )}
                </div>
            ) : null}

            {/* ── Daily Revenue Section ────────────────────────────── */}
            {!errorMsg && sortedFilteredDailyRevenue.length > 0 ? (
                <div className="accounting-section-card">
                    <SectionHeader
                        title="Daily Revenue"
                        badges={[
                            { label: `${sortedFilteredDailyRevenue.length} days` },
                            ...(selectedDates.length > 0
                                ? [{ label: `${selectedDates.length} selected`, className: "management-badge-changed" }]
                                : []),
                        ]}
                    >
                        <button
                            type="button"
                            onClick={() => setSelectedDates(sortedFilteredDailyRevenue.map((row) => row.date))}
                            disabled={selectedDates.length === sortedFilteredDailyRevenue.length}
                        >
                            Select All
                        </button>
                        <button
                            type="button"
                            onClick={() => setSelectedDates([])}
                            disabled={selectedDates.length === 0}
                        >
                            Clear
                        </button>
                        <button
                            type="button"
                            onClick={handleExportCsv}
                            disabled={selectedDailyRevenue.length === 0}
                            className="accounting-export-btn"
                        >
                            ↓ Export CSV
                        </button>
                        <button
                            type="button"
                            onClick={handleExportDetailedDailyCsv}
                            disabled={selectedDates.length === 0}
                            className="accounting-export-btn"
                        >
                            ↓ Export Detailed CSV
                        </button>
                    </SectionHeader>

                    <SelectionHint
                        count={selectedDates.length}
                        totalRevenue={selectedDailyTotal}
                        entityLabel="day(s)"
                        clearLabel="Clear selection"
                        onClear={() => setSelectedDates([])}
                    />

                    <div className="management-table-shell">
                        <table className="management-table">
                            <thead>
                                <tr>
                                    <th style={{ width: "32px" }}></th>
                                    <th style={{ width: "40px" }}></th>
                                    <th>Date</th>
                                    <th>Orders</th>
                                    <th>Order Items</th>
                                    <th>Revenue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedFilteredDailyRevenue.map((row) => {
                                    const isSelected = selectedDates.includes(row.date);
                                    const isExpanded = expandedDates.has(row.date);
                                    return (
                                        <React.Fragment key={row.date}>
                                            <tr
                                                onClick={() => toggleSelectedDate(row.date)}
                                                style={{
                                                    cursor: "pointer",
                                                    backgroundColor: isSelected ? "#eef5ff" : undefined,
                                                }}
                                            >
                                                <td
                                                    onClick={(e) => { e.stopPropagation(); toggleExpandedDate(row.date); }}
                                                    style={{ textAlign: "center", padding: "4px" }}
                                                >
                                                    <button
                                                        type="button"
                                                        className="accounting-expand-btn"
                                                        aria-label={isExpanded ? `Collapse ${row.date}` : `Expand ${row.date}`}
                                                        aria-expanded={isExpanded}
                                                    >
                                                        {isExpanded ? "▼" : "▶"}
                                                    </button>
                                                </td>
                                                <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleSelectedDate(row.date)}
                                                        aria-label={`Select day ${row.date}`}
                                                    />
                                                </td>
                                                <td>{formatDate(row.date)}</td>
                                                <td>{row.bookingCount}</td>
                                                <td>{row.itemCount}</td>
                                                <td>{row.revenue.toLocaleString("en-US")} IDR</td>
                                            </tr>
                                            {isExpanded && (
                                                <tr className="accounting-detail-row">
                                                    <td colSpan={6}>
                                                        <DayDetailPanel
                                                            date={row.date}
                                                            orderGroups={getOrderGroupsForDate(row.date)}
                                                            getGuestName={getGuestName}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr className="accounting-table-total-row">
                                    <td />
                                    <td />
                                    <td><strong>Total</strong></td>
                                    <td><strong>{sortedFilteredDailyRevenue.reduce((s, r) => s + r.bookingCount, 0)}</strong></td>
                                    <td><strong>{sortedFilteredDailyRevenue.reduce((s, r) => s + r.itemCount, 0)}</strong></td>
                                    <td><strong>{totalRevenue.toLocaleString("en-US")} IDR</strong></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ) : null}

            {/* ── Monthly Revenue Section ──────────────────────────── */}
            {!errorMsg && monthlyRevenue.length > 0 ? (
                <div className="accounting-section-card">
                    <SectionHeader
                        title="Monthly Revenue"
                        badges={[
                            { label: `${monthlyRevenue.length} months` },
                            ...(selectedMonths.length > 0
                                ? [{ label: `${selectedMonths.length} selected`, className: "management-badge-changed" }]
                                : []),
                        ]}
                    >
                        <button
                            type="button"
                            onClick={() => setSelectedMonths(monthlyRevenue.map((row) => row.month))}
                            disabled={selectedMonths.length === monthlyRevenue.length}
                        >
                            Select All
                        </button>
                        <button
                            type="button"
                            onClick={() => setSelectedMonths([])}
                            disabled={selectedMonths.length === 0}
                        >
                            Clear
                        </button>
                        <button
                            type="button"
                            onClick={handleExportMonthlyCsv}
                            disabled={selectedMonthlyRevenue.length === 0}
                            className="accounting-export-btn"
                        >
                            ↓ Export CSV
                        </button>
                        <button
                            type="button"
                            onClick={handleExportDetailedMonthlyCsv}
                            disabled={selectedMonths.length === 0}
                            className="accounting-export-btn"
                        >
                            ↓ Export Detailed CSV
                        </button>
                    </SectionHeader>

                    <SelectionHint
                        count={selectedMonths.length}
                        totalRevenue={selectedMonthlyTotal}
                        entityLabel="month(s)"
                        clearLabel="Clear selection"
                        onClear={() => setSelectedMonths([])}
                    />

                    <div className="management-table-shell">
                        <table className="management-table">
                            <thead>
                                <tr>
                                    <th style={{ width: "32px" }}></th>
                                    <th style={{ width: "40px" }}></th>
                                    <th>Month</th>
                                    <th>Days</th>
                                    <th>Orders</th>
                                    <th>Order Items</th>
                                    <th>Revenue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {monthlyRevenue.map((row) => {
                                    const isSelected = selectedMonths.includes(row.month);
                                    const isExpanded = expandedMonths.has(row.month);
                                    return (
                                        <React.Fragment key={row.month}>
                                            <tr
                                                onClick={() => toggleSelectedMonth(row.month)}
                                                style={{
                                                    cursor: "pointer",
                                                    backgroundColor: isSelected ? "#eef5ff" : undefined,
                                                }}
                                            >
                                                <td
                                                    onClick={(e) => { e.stopPropagation(); toggleExpandedMonth(row.month); }}
                                                    style={{ textAlign: "center", padding: "4px" }}
                                                >
                                                    <button
                                                        type="button"
                                                        className="accounting-expand-btn"
                                                        aria-label={isExpanded ? `Collapse ${row.month}` : `Expand ${row.month}`}
                                                        aria-expanded={isExpanded}
                                                    >
                                                        {isExpanded ? "▼" : "▶"}
                                                    </button>
                                                </td>
                                                <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleSelectedMonth(row.month)}
                                                        aria-label={`Select month ${row.month}`}
                                                    />
                                                </td>
                                                <td>{formatMonth(row.month)}</td>
                                                <td>{row.dayCount}</td>
                                                <td>{row.bookingCount}</td>
                                                <td>{row.itemCount}</td>
                                                <td>{row.revenue.toLocaleString("en-US")} IDR</td>
                                            </tr>
                                            {isExpanded && (
                                                <tr className="accounting-detail-row">
                                                    <td colSpan={7}>
                                                        <MonthDetailPanel
                                                            month={row.month}
                                                            days={getDaysForMonth(row.month)}
                                                            getOrderGroupsForDate={getOrderGroupsForDate}
                                                            getGuestName={getGuestName}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr className="accounting-table-total-row">
                                    <td />
                                    <td />
                                    <td><strong>Total</strong></td>
                                    <td><strong>{monthlyRevenue.reduce((s, r) => s + r.dayCount, 0)}</strong></td>
                                    <td><strong>{monthlyRevenue.reduce((s, r) => s + r.bookingCount, 0)}</strong></td>
                                    <td><strong>{monthlyRevenue.reduce((s, r) => s + r.itemCount, 0)}</strong></td>
                                    <td><strong>{totalRevenue.toLocaleString("en-US")} IDR</strong></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ) : null}

            {!errorMsg && categoryBreakdown.length > 0 ? (
                <div className="accounting-section-card">
                    <SectionHeader title="Category Analysis" />
                    <div className="management-table-shell">
                        <table className="management-table">
                            <thead>
                                <tr>
                                    <th>Category</th>
                                    <th>Revenue</th>
                                    <th>Revenue Share</th>
                                    <th>Items</th>
                                    <th>Item Share</th>
                                </tr>
                            </thead>
                            <tbody>
                                {categoryBreakdown.map((row) => (
                                    <tr key={row.category}>
                                        <td>{row.category}</td>
                                        <td>{row.revenue.toLocaleString("en-US")} IDR</td>
                                        <td>{row.revenueSharePct.toFixed(1)}%</td>
                                        <td>{row.items}</td>
                                        <td>{row.itemSharePct.toFixed(1)}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}

            {!errorMsg ? (
                <div className="accounting-section-card">
                    <SectionHeader title="Top / Flop Items" />
                    <div className="accounting-grid-3">
                        <div className="management-table-shell">
                            <table className="management-table">
                                <thead><tr><th colSpan={3}>Most Sold Items</th></tr></thead>
                                <tbody>
                                    {itemPerformance.topBySold.map((row) => (
                                        <tr key={`sold_${row.itemName}`}>
                                            <td>{row.itemName}</td>
                                            <td>{row.itemsSold}</td>
                                            <td>{row.revenue.toLocaleString("en-US")} IDR</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="management-table-shell">
                            <table className="management-table">
                                <thead><tr><th colSpan={3}>Highest Revenue Items</th></tr></thead>
                                <tbody>
                                    {itemPerformance.topByRevenue.map((row) => (
                                        <tr key={`rev_${row.itemName}`}>
                                            <td>{row.itemName}</td>
                                            <td>{row.itemsSold}</td>
                                            <td>{row.revenue.toLocaleString("en-US")} IDR</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="management-table-shell">
                            <table className="management-table">
                                <thead><tr><th colSpan={3}>Rarely Ordered Items</th></tr></thead>
                                <tbody>
                                    {itemPerformance.flopBySold.map((row) => (
                                        <tr key={`flop_${row.itemName}`}>
                                            <td>{row.itemName}</td>
                                            <td>{row.itemsSold}</td>
                                            <td>{row.revenue.toLocaleString("en-US")} IDR</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ) : null}

            {!errorMsg && timeSlotAnalysis.slots.length > 0 ? (
                <div className="accounting-section-card">
                    <SectionHeader
                        title="Time Slot Analysis"
                        badges={[
                            ...(timeSlotAnalysis.peakRevenue ? [{ label: `Peak Revenue: ${timeSlotAnalysis.peakRevenue.slot}` }] : []),
                            ...(timeSlotAnalysis.peakBookings ? [{ label: `Peak Bookings: ${timeSlotAnalysis.peakBookings.slot}` }] : []),
                        ]}
                    />
                    <div className="management-table-shell">
                        <table className="management-table">
                            <thead>
                                <tr>
                                    <th>Time Slot</th>
                                    <th>Bookings</th>
                                    <th>Items</th>
                                    <th>Revenue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {timeSlotAnalysis.slots.map((row) => (
                                    <tr key={row.slot}>
                                        <td>{row.slot}</td>
                                        <td>{row.bookings}</td>
                                        <td>{row.items}</td>
                                        <td>{row.revenue.toLocaleString("en-US")} IDR</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}

            {!errorMsg ? (
                <div className="accounting-section-card">
                    <SectionHeader
                        title="Guest Analysis"
                        badges={[
                            { label: `New Guests: ${guestAnalysis.newGuests}` },
                            { label: `Returning Guests: ${guestAnalysis.returningGuests}` },
                        ]}
                    />
                    <div className="accounting-grid-2">
                        <div className="management-table-shell">
                            <table className="management-table">
                                <thead>
                                    <tr>
                                        <th>Guest</th>
                                        <th>Bookings</th>
                                        <th>Revenue</th>
                                        <th>Villa</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {guestAnalysis.topGuests.map((row) => (
                                        <tr key={row.guestId}>
                                            <td>{row.guestName}</td>
                                            <td>{row.bookingCount}</td>
                                            <td>{row.revenue.toLocaleString("en-US")} IDR</td>
                                            <td>{row.villaId ?? "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="management-table-shell">
                            <table className="management-table">
                                <thead>
                                    <tr>
                                        <th>Villa</th>
                                        <th>Guests</th>
                                        <th>Bookings</th>
                                        <th>Revenue</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {guestAnalysis.byVilla.map((row) => (
                                        <tr key={row.villaId}>
                                            <td>{row.villaId}</td>
                                            <td>{row.guests}</td>
                                            <td>{row.bookings}</td>
                                            <td>{row.revenue.toLocaleString("en-US")} IDR</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ) : null}

            {!errorMsg ? (
                <div className="accounting-section-card">
                    <SectionHeader title="Trend & Forecast" />
                    <div className="accounting-grid-3">
                        <div className="accounting-kpi-card">
                            <div className="accounting-kpi-label">MoM Change</div>
                            <div className="accounting-kpi-value">{trendComparison.momPct == null ? "n/a" : `${trendComparison.momPct.toFixed(1)}%`}</div>
                        </div>
                        <div className="accounting-kpi-card">
                            <div className="accounting-kpi-label">WoW Change</div>
                            <div className="accounting-kpi-value">{trendComparison.wowPct == null ? "n/a" : `${trendComparison.wowPct.toFixed(1)}%`}</div>
                        </div>
                        <div className="accounting-kpi-card">
                            <div className="accounting-kpi-label">Month-End Forecast ({forecast.month})</div>
                            <div className="accounting-kpi-value">{Math.round(forecast.projectedMonthEndRevenue).toLocaleString("en-US")} IDR</div>
                            <div className="accounting-kpi-sub">Based on {forecast.elapsedDays}/{forecast.totalDays} days</div>
                        </div>
                    </div>
                </div>
            ) : null}

                </>
            )}

            {activeTab === "audit" && (
                <div className="accounting-section-card">
                    <SectionHeader title="Audit Log" badges={[{ label: `${auditEntries.length} events` }]} />
                    <div className="management-table-shell">
                        <table className="management-table">
                            <thead>
                                <tr>
                                    <th>Timestamp</th>
                                    <th>Actor</th>
                                    <th>Action</th>
                                    <th>Meta</th>
                                </tr>
                            </thead>
                            <tbody>
                                {auditEntries.length === 0 ? (
                                    <tr><td colSpan={4}>No audit entries yet.</td></tr>
                                ) : auditEntries.map((entry) => (
                                    <tr key={entry.id}>
                                        <td>{formatDate(entry.timestamp)} {String(entry.timestamp).slice(11, 19)}</td>
                                        <td>{entry.actor}</td>
                                        <td>{entry.action}</td>
                                        <td><code>{JSON.stringify(entry.meta)}</code></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Accounting;






